// src/pages/public/EstimatePublicView.js
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { ref as storageRef, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../Firebase";
import "./Public.css";
import Header from "../../components/Header";
import Footer from "../../components/Footer";

function safeNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function fmtDate(v) {
  if (!v) return "";
  if (typeof v?.toDate === "function") return v.toDate().toLocaleDateString();
  if (typeof v === "string") {
    const d = new Date(v);
    if (!isNaN(d.getTime())) return d.toLocaleDateString();
    return v;
  }
  return "";
}

function money(n, currency = "USD") {
  const num = Number(n);
  if (!isFinite(num)) return "";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
    }).format(num);
  } catch {
    return `$${num.toFixed(2)}`;
  }
}

function isoToday() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function joinAddress(addr) {
  if (!addr) return "";
  const parts = [
    addr.line1,
    addr.line2,
    `${addr.city || ""}${addr.city && addr.state ? ", " : ""}${addr.state || ""} ${addr.postal || ""}`.trim(),
    addr.country,
  ]
    .map((s) => String(s || "").trim())
    .filter(Boolean);
  return parts.join("\n");
}

function computeOptionTotals(option, pricing) {
  const taxRate = safeNum(pricing?.taxRate);
  const taxMode = pricing?.taxMode || "exclusive";
  const discounts = Array.isArray(pricing?.discounts) ? pricing.discounts : [];
  const fees = Array.isArray(pricing?.fees) ? pricing.fees : [];

  let subtotal = 0;
  let taxableSubtotal = 0;

  for (const g of option?.areas || []) {
    for (const it of g?.items || []) {
      const line = safeNum(it.qty) * safeNum(it.unitPrice);
      subtotal += line;
      if (it.taxable) taxableSubtotal += line;
    }
  }

  // Fees
  let feesTotal = 0;
  let taxableFees = 0;
  for (const f of fees) {
    const amt = safeNum(f.amount);
    feesTotal += amt;
    if (f.taxable) taxableFees += amt;
  }

  // Discounts on subtotal
  let discountTotal = 0;
  for (const d of discounts) {
    const type = d.type || "percent";
    const value = safeNum(d.value);
    discountTotal += type === "percent" ? subtotal * value : value;
  }
  discountTotal = Math.max(0, Math.min(discountTotal, subtotal));

  const taxableRatio = subtotal > 0 ? taxableSubtotal / subtotal : 0;
  const taxableDiscountShare = discountTotal * taxableRatio;

  const taxableAfterDiscount = Math.max(0, taxableSubtotal - taxableDiscountShare);
  const subtotalAfterDiscount = Math.max(0, subtotal - discountTotal);

  let taxTotal = 0;
  if (taxMode === "exclusive") taxTotal = (taxableAfterDiscount + taxableFees) * taxRate;

  const grandTotal = subtotalAfterDiscount + feesTotal + taxTotal;

  return { subtotal, taxableSubtotal, discountTotal, feesTotal, taxTotal, grandTotal };
}

async function resolveStorageUrl(path) {
  if (!path) return "";
  // allow full URL passthrough
  if (String(path).startsWith("http")) return path;
  const r = storageRef(storage, path);
  return await getDownloadURL(r);
}

export default function EstimatePublicView() {
  const { blobId } = useParams();

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [docData, setDocData] = useState(null);

  const [selectedOptionId, setSelectedOptionId] = useState("");
  const [resolvedPaths, setResolvedPaths] = useState({}); // {path: url}

  useEffect(() => {
    document.title = "Estimate | Crowned Roofing";
  }, []);

  useEffect(() => {
    let alive = true;

    async function load() {
      setLoading(true);
      setErr("");
      setDocData(null);

      try {
        const snap = await getDoc(doc(db, "publicEstimates", blobId));
        if (!snap.exists()) throw new Error("Estimate not found.");

        const data = snap.data();
        if (!alive) return;

        setDocData({ id: snap.id, ...data });

        // default option:
        const acceptedOpt = data?.acceptance?.selectedOptionId;
        const firstOpt = (data?.options || [])[0]?.optionId;
        setSelectedOptionId(acceptedOpt || firstOpt || "A");
      } catch (e) {
        if (!alive) return;
        setErr(e?.message || "Failed to load estimate.");
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [blobId]);

  const options = docData?.options || [];
  const pricing = docData?.pricing || {};
  const currency = docData?.currency || "USD";

  const selectedOption = useMemo(() => {
    return options.find((o) => o.optionId === selectedOptionId) || options[0] || null;
  }, [options, selectedOptionId]);

  const totals = useMemo(() => {
    if (!selectedOption) return null;
    // Use stored totals if present; otherwise compute
    const hasStored =
      selectedOption?.totals && typeof selectedOption.totals.grandTotal !== "undefined";
    return hasStored ? selectedOption.totals : computeOptionTotals(selectedOption, pricing);
  }, [selectedOption, pricing]);

  // Resolve any storage paths found in attachments + item photos (best-effort)
  useEffect(() => {
    let alive = true;

    async function collectAndResolve() {
      if (!docData) return;

      const paths = new Set();

      // attachments
      (docData.attachments || []).forEach((a) => {
        if (a?.path) paths.add(a.path);
      });

      // item photos
      (docData.options || []).forEach((o) => {
        (o.areas || []).forEach((g) => {
          (g.items || []).forEach((it) => {
            (it.photos || []).forEach((p) => {
              if (p?.path) paths.add(p.path);
              if (p?.url) paths.add(p.url);
            });
          });
        });
      });

      const unresolved = [...paths].filter((p) => !resolvedPaths[p]);
      if (!unresolved.length) return;

      const next = {};
      for (const p of unresolved) {
        try {
          next[p] = await resolveStorageUrl(p);
        } catch {
          // ignore
        }
      }

      if (!alive) return;
      setResolvedPaths((prev) => ({ ...prev, ...next }));
    }

    collectAndResolve();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docData]);

const handlePrint = (e) => {
  if (e) {
    e.preventDefault();
    e.stopPropagation();
  }

  // Let the current render/layout settle before printing
  requestAnimationFrame(() => {
    window.print();
  });
};


  if (loading) {
    return (
      <div className="estWrap">
        <div className="estCard">
          <div className="estMuted">Loading estimate…</div>
        </div>
      </div>
    );
  }

  if (err) {
    return (
      <div className="estWrap">
        <div className="estCard">
          <h1 className="estH1">Estimate</h1>
          <div className="estError">{err}</div>
        </div>
      </div>
    );
  }

  if (!docData) return null;

  const issuer = docData.issuer || {};
  const customer = docData.customer || {};
  const site = docData.site || {};

  const showDiscount = (pricing.discounts || []).some((d) => safeNum(d.value) > 0);
  const showFees = (pricing.fees || []).some((f) => safeNum(f.amount) > 0);
  const showTax =
    (pricing.taxMode || "exclusive") === "exclusive" && safeNum(pricing.taxRate) > 0;

  return (
    <div className="estWrap">
      {/* Make Header/Footer non-print to avoid blank print pages caused by sticky/fixed/filters */}
      <div className="noPrint">
        <Header />
      </div>

      {/* Non-print toolbar */}
      <div className="estToolbar noPrint">
        <div className="estToolbarInner">
          <div className="estToolbarLeft">
            <div className="estTiny">Blob ID</div>
            <div className="estMono">{docData.id}</div>
          </div>

          <div className="estToolbarRight">
            {options.length > 0 ? (
              <select
                className="estSelect"
                value={selectedOptionId}
                onChange={(e) => setSelectedOptionId(e.target.value)}
              >
                {options.map((o) => (
                  <option key={o.optionId} value={o.optionId}>
                    {o.optionId} — {o.title || `Option ${o.optionId}`}
                  </option>
                ))}
              </select>
            ) : null}

            <button className="estBtn" onClick={handlePrint}>
              Print / Save PDF
            </button>
          </div>
        </div>
      </div>

      {/* Printable document */}
      <div className="estDoc">
        <div className="estCard estCardPrint">
          <img
            src="/images/logo-black.png"
            alt=""
            aria-hidden="true"
            className="estWatermark"
          />
          <header className="estHeader">
            <div className="estBrand">
              <img
                src="/images/logo.png"
                alt="Crowned Roofing"
                className="estLogo"
              />
              <div className="estCompany">{issuer.companyName || "Crowned Roofing"}</div>
              <div className="estMeta">
                <div>{issuer.website || ""}</div>
                <div>{issuer.email || ""}</div>
                <div>{issuer.phone || ""}</div>
              </div>
            </div>

            <div className="estHeaderRight">
              <div className="estTitle">Estimate</div>
              <div className="estHeaderGrid">
                <div className="estTiny">Estimate #</div>
                <div className="estValue">{docData.estimateNumber || docData.id}</div>

                <div className="estTiny">Revision</div>
                <div className="estValue">{docData.revision || 1}</div>

                <div className="estTiny">Valid Until</div>
                <div className="estValue">{fmtDate(docData.validUntil) || "—"}</div>

                <div className="estTiny">Status</div>
                <div className="estValue">{String(docData.status || "draft")}</div>
              </div>
            </div>
          </header>

          <section className="estTwoCol">
            <div className="estBlock">
              <div className="estBlockTitle">Bill To</div>
              <div className="estBlockBody">
                <div className="estStrong">{customer.companyName || customer.name || "—"}</div>
                {customer.name && customer.companyName ? (
                  <div className="estMuted">{customer.name}</div>
                ) : null}
                <div className="estLine">{customer.phone || ""}</div>
                <div className="estLine">{customer.email || ""}</div>
                <pre className="estPre">{joinAddress(customer.billingAddress)}</pre>
              </div>
            </div>

            <div className="estBlock">
              <div className="estBlockTitle">Site</div>
              <div className="estBlockBody">
                <div className="estStrong">{site.propertyName || "—"}</div>
                <pre className="estPre">{joinAddress(site.siteAddress)}</pre>
                {site.approxArea ? (
                  <div className="estLine">Approx. Area: {site.approxArea}</div>
                ) : null}
                {Array.isArray(site.roofSystems) && site.roofSystems.length ? (
                  <div className="estLine">Roof: {site.roofSystems.join(", ")}</div>
                ) : null}
              </div>
            </div>
          </section>

          {selectedOption ? (
            <section className="estOption printBreakAfter">
              <div className="estOptionTop">
                <div>
                  <div className="estBlockTitle">
                    {selectedOption.optionId} —{" "}
                    {selectedOption.title || `Option ${selectedOption.optionId}`}
                  </div>
                  {selectedOption.summary ? (
                    <div className="estMuted" style={{ marginTop: 6 }}>
                      {selectedOption.summary}
                    </div>
                  ) : null}
                </div>

                <div className="estOptionTotals">
                  <div className="estTiny">Grand Total</div>
                  <div className="estGrand">{money(totals?.grandTotal, currency) || "—"}</div>
                </div>
              </div>

              <div className="estLists">
                {Array.isArray(selectedOption.included) && selectedOption.included.length ? (
                  <div className="estList">
                    <div className="estTiny estCaps">Included</div>
                    <ul>
                      {selectedOption.included.map((x, i) => (
                        <li key={i}>{x}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {Array.isArray(selectedOption.excluded) && selectedOption.excluded.length ? (
                  <div className="estList">
                    <div className="estTiny estCaps">Excluded</div>
                    <ul>
                      {selectedOption.excluded.map((x, i) => (
                        <li key={i}>{x}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            </section>
          ) : null}

          {/* Scope Table */}
          {selectedOption?.areas?.length ? (
            <section className="estScope">
              {selectedOption.areas.map((g) => (
                <div className="estGroup" key={g.groupId}>
                  <div className="estGroupHead">
                    <div className="estGroupTitle">{g.title || g.groupId}</div>
                    {g.notes ? <div className="estMuted">{g.notes}</div> : null}
                  </div>

                  <table className="estTable">
                    <thead>
                      <tr>
                        <th className="colDesc">Description</th>
                        <th className="colQty">Qty</th>
                        <th className="colUnit">Unit</th>
                        <th className="colRate">Unit Price</th>
                        <th className="colTotal">Line Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(g.items || []).map((it) => {
                        const lineTotal = safeNum(it.qty) * safeNum(it.unitPrice);
                        return (
                          <tr key={it.itemId}>
                            <td className="colDesc">
                              <div className="estStrong">{it.name || it.itemId}</div>
                              {it.description ? (
                                <div className="estMuted">{it.description}</div>
                              ) : null}

                              {(it.photos || []).length ? (
                                <div className="estPhotoRow">
                                  {it.photos.slice(0, 3).map((p, idx) => {
                                    const src =
                                      p.url ||
                                      resolvedPaths[p.path] ||
                                      resolvedPaths[p.url] ||
                                      "";
                                    if (!src) return null;
                                    return (
                                      <img
                                        key={idx}
                                        src={src}
                                        alt={p.caption || "Estimate photo"}
                                        className="estPhoto"
                                        loading="lazy"
                                      />
                                    );
                                  })}
                                </div>
                              ) : null}
                            </td>
                            <td className="colQty">{safeNum(it.qty)}</td>
                            <td className="colUnit">{it.unit || ""}</td>
                            <td className="colRate">{money(it.unitPrice, currency)}</td>
                            <td className="colTotal">{money(lineTotal, currency)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ))}
            </section>
          ) : null}

          {/* Totals */}
          <section className="estTotals printBreakAfter">
            <div className="estTotalsBox">
              <div className="row">
                <div>Subtotal</div>
                <div>{money(totals?.subtotal, currency)}</div>
              </div>

              {showDiscount ? (
                <div className="row">
                  <div>Discounts</div>
                  <div>-{money(totals?.discountTotal, currency)}</div>
                </div>
              ) : null}

              {showFees ? (
                <div className="row">
                  <div>Fees</div>
                  <div>{money(totals?.feesTotal, currency)}</div>
                </div>
              ) : null}

              {showTax ? (
                <div className="row">
                  <div>Tax</div>
                  <div>{money(totals?.taxTotal, currency)}</div>
                </div>
              ) : null}

              <div className="row grand">
                <div>Grand Total</div>
                <div>{money(totals?.grandTotal, currency)}</div>
              </div>
            </div>
          </section>

          {/* Schedule + Payment + Warranty */}
          <section className="estThreeCol">
            {docData.schedule ? (
              <div className="estBlock">
                <div className="estBlockTitle">Schedule</div>
                <div className="estBlockBody">
                  {docData.schedule.leadTime ? (
                    <div>
                      <span className="estTiny">Lead Time:</span> {docData.schedule.leadTime}
                    </div>
                  ) : null}
                  {docData.schedule.duration ? (
                    <div>
                      <span className="estTiny">Duration:</span> {docData.schedule.duration}
                    </div>
                  ) : null}
                  {docData.schedule.workWindow ? (
                    <div>
                      <span className="estTiny">Work Window:</span>{" "}
                      {docData.schedule.workWindow}
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}

            {docData.payment ? (
              <div className="estBlock">
                <div className="estBlockTitle">Payment</div>
                <div className="estBlockBody">
                  {docData.payment.depositRequired ? (
                    <div>
                      <span className="estTiny">Deposit:</span>{" "}
                      {docData.payment.depositType === "percent"
                        ? `${Math.round(safeNum(docData.payment.depositValue) * 100)}%`
                        : money(docData.payment.depositValue, currency)}
                    </div>
                  ) : (
                    <div>
                      <span className="estTiny">Deposit:</span> Not required
                    </div>
                  )}

                  {Array.isArray(docData.payment.paymentMethods) &&
                  docData.payment.paymentMethods.length ? (
                    <div style={{ marginTop: 6 }}>
                      <span className="estTiny">Methods:</span>{" "}
                      {docData.payment.paymentMethods.join(", ")}
                    </div>
                  ) : null}

                  {docData.payment.invoicing ? (
                    <div style={{ marginTop: 6 }} className="estMuted">
                      {docData.payment.invoicing}
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}

            {docData.warranty ? (
              <div className="estBlock">
                <div className="estBlockTitle">Warranty</div>
                <div className="estBlockBody">
                  <div className="estStrong">{docData.warranty.type || ""}</div>
                  {docData.warranty.term ? (
                    <div className="estMuted">{docData.warranty.term}</div>
                  ) : null}
                  {Array.isArray(docData.warranty.details) &&
                  docData.warranty.details.length ? (
                    <ul className="estCompactList">
                      {docData.warranty.details.map((x, i) => (
                        <li key={i}>{x}</li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              </div>
            ) : null}
          </section>

          {/* Legal blocks */}
          <section className="estLegal">
            {Array.isArray(docData.assumptions) && docData.assumptions.length ? (
              <div className="estBlock">
                <div className="estBlockTitle">Assumptions</div>
                <ul className="estCompactList">
                  {docData.assumptions.map((x, i) => (
                    <li key={i}>{x}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {Array.isArray(docData.exclusions) && docData.exclusions.length ? (
              <div className="estBlock">
                <div className="estBlockTitle">Exclusions</div>
                <ul className="estCompactList">
                  {docData.exclusions.map((x, i) => (
                    <li key={i}>{x}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {Array.isArray(docData.terms) && docData.terms.length ? (
              <div className="estBlock">
                <div className="estBlockTitle">Terms</div>
                <ul className="estCompactList">
                  {docData.terms.map((x, i) => (
                    <li key={i}>{x}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </section>

          {/* Attachments (screen only) */}
          {Array.isArray(docData.attachments) && docData.attachments.length ? (
            <section className="estAttachments noPrint">
              <div className="estBlockTitle">Attachments</div>
              <div className="estAttachList">
                {docData.attachments.map((a, i) => {
                  const href = a.url || resolvedPaths[a.path] || "";
                  return (
                    <a
                      key={i}
                      className="estAttach"
                      href={href || "#"}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => {
                        if (!href) e.preventDefault();
                      }}
                    >
                      <div className="estStrong">{a.label || a.path}</div>
                      <div className="estMuted">{a.caption || a.type || ""}</div>
                    </a>
                  );
                })}
              </div>
            </section>
          ) : null}

          <footer className="estFooter">
            <div className="estMuted">
              Prepared by {issuer.companyName || "Crowned Roofing"}
              {issuer.preparedBy?.name ? ` — ${issuer.preparedBy.name}` : ""}
            </div>
            <div className="estTiny">
              Generated: {fmtDate(docData.updatedAt) || fmtDate(docData.createdAt) || isoToday()}
            </div>
          </footer>
        </div>
      </div>

      <div className="noPrint">
        <Footer />
      </div>
    </div>
  );
}